import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const AccountOrders = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (user) {
      fetchOrders();
    }
  }, [user]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/orders/user/${user.id}`);
      setOrders(response.data || []);
    } catch (error) {
      console.error('Erreur chargement commandes:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-600',
      confirmed: 'bg-blue-100 text-blue-600',
      preparing: 'bg-purple-100 text-purple-600',
      shipped: 'bg-indigo-100 text-indigo-600',
      delivered: 'bg-green-100 text-green-600',
      cancelled: 'bg-red-100 text-red-600'
    };
    const labels = {
      pending: 'En attente',
      confirmed: 'Confirmée',
      preparing: 'Préparation',
      shipped: 'Expédiée',
      delivered: 'Livrée',
      cancelled: 'Annulée'
    };
    return <span className={`px-2 py-1 rounded-full text-xs ${badges[status] || 'bg-gray-100 text-gray-600'}`}>{labels[status] || status}</span>;
  };

  const filteredOrders = orders.filter(order => {
    if (filter === 'all') return true;
    return order.status === filter;
  });

  if (!user) {
    return (
      <div className="container-custom py-20 text-center">
        <h2 className="text-2xl font-bold mb-4">Connectez-vous</h2>
        <Link to="/login" className="btn-primary inline-block">Se connecter</Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container-custom py-20 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
        <p className="mt-4 text-gray-500">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="container-custom py-12">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Mes commandes</h1>
        <Link to="/account" className="text-secondary hover:underline">← Retour au compte</Link>
      </div>

      {/* Filtres */}
      <div className="flex gap-2 mb-6 flex-wrap">
        <button onClick={() => setFilter('all')} className={`px-4 py-2 rounded-lg transition ${filter === 'all' ? 'bg-primary text-white' : 'bg-gray-200'}`}>Toutes</button>
        <button onClick={() => setFilter('pending')} className={`px-4 py-2 rounded-lg transition ${filter === 'pending' ? 'bg-primary text-white' : 'bg-gray-200'}`}>En attente</button>
        <button onClick={() => setFilter('confirmed')} className={`px-4 py-2 rounded-lg transition ${filter === 'confirmed' ? 'bg-primary text-white' : 'bg-gray-200'}`}>Confirmée</button>
        <button onClick={() => setFilter('preparing')} className={`px-4 py-2 rounded-lg transition ${filter === 'preparing' ? 'bg-primary text-white' : 'bg-gray-200'}`}>Préparation</button>
        <button onClick={() => setFilter('shipped')} className={`px-4 py-2 rounded-lg transition ${filter === 'shipped' ? 'bg-primary text-white' : 'bg-gray-200'}`}>Expédiée</button>
        <button onClick={() => setFilter('delivered')} className={`px-4 py-2 rounded-lg transition ${filter === 'delivered' ? 'bg-primary text-white' : 'bg-gray-200'}`}>Livrée</button>
        <button onClick={() => setFilter('cancelled')} className={`px-4 py-2 rounded-lg transition ${filter === 'cancelled' ? 'bg-primary text-white' : 'bg-gray-200'}`}>Annulée</button>
      </div>

      {/* Liste des commandes */}
      {filteredOrders.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-12 text-center">
          <p className="text-gray-500 mb-4">Aucune commande trouvée</p>
          <Link to="/products" className="btn-primary inline-block">Découvrir nos produits</Link>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredOrders.map(order => (
            <div key={order.id} className="bg-white rounded-xl shadow-md p-5 hover:shadow-lg transition">
              <div className="flex flex-wrap justify-between items-start gap-4">
                <div>
                  <p className="font-bold text-lg">{order.order_number}</p>
                  <p className="text-sm text-gray-500">{new Date(order.created_at).toLocaleDateString()} à {new Date(order.created_at).toLocaleTimeString()}</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-primary text-xl">{order.grand_total} €</p>
                  {getStatusBadge(order.status)}
                </div>
              </div>
              <div className="mt-3 pt-3 border-t flex justify-between items-center">
                <p className="text-sm text-gray-600">{order.items?.length || 0} article(s)</p>
                <button onClick={() => setSelectedOrder(order)} className="text-secondary hover:underline text-sm">Voir les détails</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Détails commande */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Commande #{selectedOrder.order_number}</h2>
              <button onClick={() => setSelectedOrder(null)} className="text-gray-500 text-2xl">✕</button>
            </div>
            <div className="mb-4">
              <p className="text-sm text-gray-500">Passée le {new Date(selectedOrder.created_at).toLocaleDateString()} à {new Date(selectedOrder.created_at).toLocaleTimeString()}</p>
              <p className="mt-2">Statut : {getStatusBadge(selectedOrder.status)}</p>
            </div>
            <div className="border-t pt-4">
              <h3 className="font-semibold mb-2">Articles</h3>
              {selectedOrder.items?.map((item, idx) => (
                <div key={idx} className="flex justify-between py-2 border-b">
                  <span>{item.name} x{item.quantity}</span>
                  <span className="font-bold">{(item.price * item.quantity).toFixed(2)} €</span>
                </div>
              ))}
              <div className="mt-3 pt-3 border-t">
                <div className="flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <span className="text-primary">{selectedOrder.grand_total} €</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountOrders;